import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { NumberInput } from "lib/components/ui/number-input/number-input.component";
import {
  MonetizationOn,
  Save,
  Info,
  Storage,
  AccessTime,
  CheckCircle,
} from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useReadPaymentSettingsQuery,
  useUpdatePaymentSettingsMutation,
} from "api/endpoints/settings";
import { toast } from "react-toastify";

const priceFieldValidation = Yup.string().test(
  "is-valid-price",
  "قیمت باید عدد معتبر باشد",
  function (value) {
    if (!value) return false;
    const valueWithNoSeparator = value.replace(/,/g, "");
    const numericValue = Number(valueWithNoSeparator);
    return !isNaN(numericValue) && numericValue >= 0;
  }
);

const validationSchema = Yup.object({
  gigLimitTimeUnlimitedPrice: priceFieldValidation.required(
    "قیمت پلن حجم محدود الزامی است"
  ),
  gigUnlimitedTimeLimitPrice: priceFieldValidation.required(
    "قیمت پلن زمان محدود الزامی است"
  ),
  gigLimitTimeLimitPrice: priceFieldValidation.required(
    "قیمت پلن اقتصادی الزامی است"
  ),
  gigLimitTimeUnlimitedEnabled: Yup.boolean(),
  gigUnlimitedTimeLimitEnabled: Yup.boolean(),
  gigLimitTimeLimitEnabled: Yup.boolean(),
  v2rayEnabled: Yup.boolean(),
});

export const SettingsV2RayTariffsComponent = ({
  embedded = false,
}: {
  embedded?: boolean;
}) => {
  const { data: settings, isLoading, error } = useReadPaymentSettingsQuery();
  const [updateSettings, { isLoading: updating, error: updateError }] =
    useUpdatePaymentSettingsMutation();

  const formatNumberWithSeparators = (value: number | string): string => {
    if (!value) return "";
    const numValue = typeof value === "string" ? Number(value) : value;
    return numValue.toLocaleString();
  };

  const parseNumberFromString = (value: string): number => {
    const valueWithNoSeparator = value.replace(/,/g, "");
    return Number(valueWithNoSeparator);
  };

  const handleSubmit = async (values: {
    gigLimitTimeUnlimitedPrice: number;
    gigUnlimitedTimeLimitPrice: number;
    gigLimitTimeLimitPrice: number;
    gigLimitTimeUnlimitedEnabled: boolean;
    gigUnlimitedTimeLimitEnabled: boolean;
    gigLimitTimeLimitEnabled: boolean;
    v2rayEnabled: boolean;
  }) => {
    try {
      await toast.promise(
        updateSettings({ payload: values }).unwrap(),
        {
          error: {
            render:
              "هنگام بروزرسانی تنظیمات مشکلی بوجود آمد، لطفا با پشتیبانی ارتباط برقرار نمایید.",
          },
          pending: {
            render: "درحال بروزرسانی تنظیمات، لطفا منتظر بمانید...",
            autoClose: 5000,
          },
          success: "تنظیمات با موفقیت بروزرسانی شد.",
        }
      );
    } catch (err) {
      console.error("Failed to update settings:", err);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        خطا در بارگذاری تنظیمات
      </Alert>
    );
  }

  return (
    <Box p={embedded ? 0 : 3}>
      {!embedded && (
        <Typography variant="h4" gutterBottom>
          تعرفه V2Ray
        </Typography>
      )}

      <Paper sx={{ p: 3 }}>
        {updateError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            خطا در به‌روزرسانی تنظیمات
          </Alert>
        )}

        <Formik
          initialValues={{
            gigLimitTimeUnlimitedPrice: formatNumberWithSeparators(
              settings?.gigLimitTimeUnlimitedPrice || 0
            ),
            gigUnlimitedTimeLimitPrice: formatNumberWithSeparators(
              settings?.gigUnlimitedTimeLimitPrice || 0
            ),
            gigLimitTimeLimitPrice: formatNumberWithSeparators(
              settings?.gigLimitTimeLimitPrice || 0
            ),
            gigLimitTimeUnlimitedEnabled:
              settings?.gigLimitTimeUnlimitedEnabled ?? true,
            gigUnlimitedTimeLimitEnabled:
              settings?.gigUnlimitedTimeLimitEnabled ?? true,
            gigLimitTimeLimitEnabled:
              settings?.gigLimitTimeLimitEnabled ?? true,
            v2rayEnabled: settings?.v2rayEnabled ?? true,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleSubmit({
              gigLimitTimeUnlimitedPrice: parseNumberFromString(
                values.gigLimitTimeUnlimitedPrice.toString()
              ),
              gigUnlimitedTimeLimitPrice: parseNumberFromString(
                values.gigUnlimitedTimeLimitPrice.toString()
              ),
              gigLimitTimeLimitPrice: parseNumberFromString(
                values.gigLimitTimeLimitPrice.toString()
              ),
              gigLimitTimeUnlimitedEnabled: values.gigLimitTimeUnlimitedEnabled,
              gigUnlimitedTimeLimitEnabled: values.gigUnlimitedTimeLimitEnabled,
              gigLimitTimeLimitEnabled: values.gigLimitTimeLimitEnabled,
              v2rayEnabled: values.v2rayEnabled,
            });
          }}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        فعال/غیرفعال کردن V2Ray
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.v2rayEnabled}
                            onChange={(e) =>
                              setFieldValue("v2rayEnabled", e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label="نمایش V2Ray در صفحه خرید"
                      />
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        تعرفه قیمت‌ها
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <NumberInput
                            fullWidth
                            name="gigLimitTimeUnlimitedPrice"
                            label="حجم محدود - زمان نامحدود"
                            value={values.gigLimitTimeUnlimitedPrice}
                            onChange={(e) => {
                              const numericValue = parseNumberFromString(
                                e.target.value
                              );
                              setFieldValue(
                                "gigLimitTimeUnlimitedPrice",
                                formatNumberWithSeparators(numericValue)
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.gigLimitTimeUnlimitedPrice &&
                              Boolean(errors.gigLimitTimeUnlimitedPrice)
                            }
                            helperText={
                              touched.gigLimitTimeUnlimitedPrice &&
                              errors.gigLimitTimeUnlimitedPrice
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MonetizationOn />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  تومان
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <NumberInput
                            fullWidth
                            name="gigUnlimitedTimeLimitPrice"
                            label="حجم نامحدود - زمان محدود"
                            value={values.gigUnlimitedTimeLimitPrice}
                            onChange={(e) => {
                              const numericValue = parseNumberFromString(
                                e.target.value
                              );
                              setFieldValue(
                                "gigUnlimitedTimeLimitPrice",
                                formatNumberWithSeparators(numericValue)
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.gigUnlimitedTimeLimitPrice &&
                              Boolean(errors.gigUnlimitedTimeLimitPrice)
                            }
                            helperText={
                              touched.gigUnlimitedTimeLimitPrice &&
                              errors.gigUnlimitedTimeLimitPrice
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MonetizationOn />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  تومان
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <NumberInput
                            fullWidth
                            name="gigLimitTimeLimitPrice"
                            label="حجم محدود - زمان محدود"
                            value={values.gigLimitTimeLimitPrice}
                            onChange={(e) => {
                              const numericValue = parseNumberFromString(
                                e.target.value
                              );
                              setFieldValue(
                                "gigLimitTimeLimitPrice",
                                formatNumberWithSeparators(numericValue)
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.gigLimitTimeLimitPrice &&
                              Boolean(errors.gigLimitTimeLimitPrice)
                            }
                            helperText={
                              touched.gigLimitTimeLimitPrice &&
                              errors.gigLimitTimeLimitPrice
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MonetizationOn />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  تومان
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        فعال/غیرفعال کردن پلن‌ها
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        پلن‌های غیرفعال در صفحه خرید نمایش داده نمی‌شوند
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.gigLimitTimeUnlimitedEnabled}
                                onChange={(e) =>
                                  setFieldValue(
                                    "gigLimitTimeUnlimitedEnabled",
                                    e.target.checked
                                  )
                                }
                                color="primary"
                              />
                            }
                            label="حجم محدود - زمان نامحدود"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.gigUnlimitedTimeLimitEnabled}
                                onChange={(e) =>
                                  setFieldValue(
                                    "gigUnlimitedTimeLimitEnabled",
                                    e.target.checked
                                  )
                                }
                                color="primary"
                              />
                            }
                            label="حجم نامحدود - زمان محدود"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.gigLimitTimeLimitEnabled}
                                onChange={(e) =>
                                  setFieldValue(
                                    "gigLimitTimeLimitEnabled",
                                    e.target.checked
                                  )
                                }
                                color="primary"
                              />
                            }
                            label="حجم محدود - زمان محدود"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{ bgcolor: "info.light", color: "info.contrastText" }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Info sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          فرمول محاسبه قیمت نهایی
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Card sx={{ bgcolor: "background.paper", color: "text.primary" }}>
                            <CardContent>
                              <Box display="flex" alignItems="center" mb={2}>
                                <Storage sx={{ mr: 1, color: "primary.main" }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                  حجم محدود - زمان نامحدود
                                </Typography>
                              </Box>
                              <Chip label="قیمت در هر گیگابایت" size="small" color="primary" variant="outlined" />
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Card sx={{ bgcolor: "background.paper", color: "text.primary" }}>
                            <CardContent>
                              <Box display="flex" alignItems="center" mb={2}>
                                <AccessTime sx={{ mr: 1, color: "primary.main" }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                  حجم نامحدود - زمان محدود
                                </Typography>
                              </Box>
                              <Chip label="قیمت در هر ماه" size="small" color="primary" variant="outlined" />
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Card sx={{ bgcolor: "background.paper", color: "text.primary" }}>
                            <CardContent>
                              <Box display="flex" alignItems="center" mb={2}>
                                <CheckCircle sx={{ mr: 1, color: "primary.main" }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                  حجم محدود - زمان محدود
                                </Typography>
                              </Box>
                              <Chip label="قیمت در هر گیگابایت" size="small" color="primary" variant="outlined" />
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 2 }} />
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          قیمت‌های وارد شده، قیمت پایه برای هر واحد هستند و قیمت نهایی بر اساس انتخاب کاربر محاسبه می‌شود.
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<Save className="icon-btn" />}
                      disabled={updating}
                    >
                      {updating ? "در حال ذخیره..." : "ذخیره تنظیمات"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};
